import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Lightbulb,
  User,
  Calendar,
  AlertCircle,
  CheckCircle,
  Clock,
  ArrowRight,
  MessageSquare,
  TrendingUp,
  Shield
} from 'lucide-react';

// Sample insight data
const sampleInsight = {
  id: "ins_abc123",
  description: "Critical: 40.61% transaction failure rate in November",
  details: "Analysis of 40,169 November transactions reveals an alarming 40.61% failure rate (16,281 failed transactions).",
  status: "new",
  priority: "high",
  category: "risk_analysis",
  type: "alert",
  assigned_to: "Risk Management Team",
  date: "2025-11-15T00:00:00Z",
  comments: [{}, {}]
};

const AIInsightCardDesigns = () => {
  const [selectedDesign, setSelectedDesign] = useState('current');

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-orange-100 text-orange-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'fraud_detection': return <Shield className="w-5 h-5" />;
      case 'revenue_optimization': return <TrendingUp className="w-5 h-5" />;
      case 'risk_analysis': return <AlertCircle className="w-5 h-5" />;
      default: return <Lightbulb className="w-5 h-5" />;
    }
  };

  // Design 1: Current Design (Left Border)
  const CurrentDesign = () => (
    <Card
      className="p-5 hover:shadow-lg transition-shadow cursor-pointer border-l-4"
      style={{ borderLeftColor: '#ef4444' }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          <Badge className={`${getStatusBadgeColor('new')} text-xs`}>new</Badge>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 font-mono">#ABC123</span>
          <Badge className={`${getPriorityBadgeColor('high')} text-xs`}>high</Badge>
        </div>
      </div>
      <h3 className="font-semibold text-lg mb-2 line-clamp-2">{sampleInsight.description}</h3>
      <p className="text-sm text-gray-600 mb-4 line-clamp-3">{sampleInsight.details}</p>
      <div className="space-y-2 text-xs text-gray-500">
        <div className="flex items-center gap-2">
          <span className="font-medium">Risk Analysis</span>
          <span>•</span>
          <span>Alert</span>
        </div>
        <div className="flex items-center gap-2">
          <User className="w-3 h-3" />
          <span>{sampleInsight.assigned_to}</span>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-3 h-3" />
          <span>Nov 15, 2025</span>
        </div>
        <div className="text-blue-600">2 comments</div>
      </div>
    </Card>
  );

  // Design 2: Horizontal Compact
  const HorizontalDesign = () => (
    <Card className="p-4 hover:shadow-lg transition-shadow cursor-pointer">
      <div className="flex items-center gap-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            {getCategoryIcon('risk_analysis')}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-mono text-gray-500">#ABC123</span>
            <Badge className={`${getStatusBadgeColor('new')} text-xs`}>new</Badge>
            <Badge className={`${getPriorityBadgeColor('high')} text-xs`}>high</Badge>
          </div>
          <h3 className="font-semibold text-base mb-1 line-clamp-1">{sampleInsight.description}</h3>
          <p className="text-sm text-gray-600 line-clamp-2 mb-2">{sampleInsight.details}</p>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1"><User className="w-3 h-3" />{sampleInsight.assigned_to}</span>
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Nov 15</span>
            <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />2</span>
          </div>
        </div>
        <div className="flex-shrink-0">
          <Button variant="ghost" size="sm">
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </Card>
  );

  // Design 3: Minimalist with Top Border
  const MinimalistDesign = () => (
    <Card className="p-5 hover:shadow-lg transition-shadow cursor-pointer border-t-4 border-t-red-500">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded bg-red-100 flex items-center justify-center">
            <AlertCircle className="w-4 h-4 text-red-600" />
          </div>
          <span className="text-xs font-mono text-gray-500">#ABC123</span>
        </div>
        <div className="flex gap-1">
          <Badge className={`${getStatusBadgeColor('new')} text-xs`}>new</Badge>
          <Badge className={`${getPriorityBadgeColor('high')} text-xs`}>HIGH</Badge>
        </div>
      </div>
      <h3 className="font-bold text-lg mb-2">{sampleInsight.description}</h3>
      <p className="text-sm text-gray-600 mb-4 line-clamp-2">{sampleInsight.details}</p>
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <User className="w-3 h-3" />
            Risk Mgmt
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            Nov 15
          </span>
        </div>
        <span className="text-xs text-blue-600">2 comments →</span>
      </div>
    </Card>
  );

  // Design 4: Card with Color Header
  const ColorHeaderDesign = () => (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer">
      <div className="h-2 bg-gradient-to-r from-red-500 to-red-600"></div>
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Badge className={`${getStatusBadgeColor('new')} text-xs`}>NEW</Badge>
            <Badge className={`${getPriorityBadgeColor('high')} text-xs`}>HIGH</Badge>
          </div>
          <span className="text-xs font-mono text-gray-400">#ABC123</span>
        </div>
        <div className="flex items-start gap-3 mb-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
            <AlertCircle className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h3 className="font-semibold text-base mb-1 leading-tight">{sampleInsight.description}</h3>
            <p className="text-sm text-gray-600 line-clamp-2">{sampleInsight.details}</p>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-2 text-gray-500">
            <User className="w-3 h-3" />
            <span>{sampleInsight.assigned_to}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-gray-400">Nov 15, 2025</span>
            <span className="flex items-center gap-1 text-blue-600">
              <MessageSquare className="w-3 h-3" />
              2
            </span>
          </div>
        </div>
      </div>
    </Card>
  );

  // Design 5: Ticket Style with Shadow
  const TicketDesign = () => (
    <Card className="p-5 hover:shadow-2xl transition-all cursor-pointer border-0 shadow-md relative overflow-hidden">
      <div className="absolute top-0 right-0 w-20 h-20 bg-red-500 opacity-5 rounded-bl-full"></div>
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">#ABC123</span>
          <div className="flex gap-1">
            <Badge className="bg-red-500 text-white text-xs">HIGH</Badge>
            <Badge className={`${getStatusBadgeColor('new')} text-xs`}>NEW</Badge>
          </div>
        </div>
        <div className="flex items-start gap-3 mb-3">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg">
              <AlertCircle className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="flex-1">
            <span className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Risk Analysis • Alert</span>
            <h3 className="font-bold text-lg mt-1 mb-2">{sampleInsight.description}</h3>
            <p className="text-sm text-gray-600 line-clamp-2">{sampleInsight.details}</p>
          </div>
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2 text-xs">
            <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">R</div>
            <span className="text-gray-700 font-medium">Risk Mgmt Team</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span>Nov 15, 2025</span>
            <span className="flex items-center gap-1 text-blue-600 font-medium">
              <MessageSquare className="w-3 h-3" />
              2 comments
            </span>
          </div>
        </div>
      </div>
    </Card>
  );

  const designs = [
    { id: 'current', name: 'Current Design', component: <CurrentDesign /> },
    { id: 'horizontal', name: 'Horizontal Compact', component: <HorizontalDesign /> },
    { id: 'minimalist', name: 'Minimalist Top Border', component: <MinimalistDesign /> },
    { id: 'header', name: 'Color Header', component: <ColorHeaderDesign /> },
    { id: 'ticket', name: 'Enhanced Ticket Style', component: <TicketDesign /> }
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Lightbulb className="w-8 h-8 text-yellow-500" />
          AI Insights Card Design Options
        </h1>
        <p className="text-gray-600 mt-2">
          Select a design style for your insights cards
        </p>
      </div>

      {/* Design Selector */}
      <div className="flex gap-2 mb-8 flex-wrap">
        {designs.map((design) => (
          <Button
            key={design.id}
            variant={selectedDesign === design.id ? 'default' : 'outline'}
            onClick={() => setSelectedDesign(design.id)}
          >
            {design.name}
          </Button>
        ))}
      </div>

      {/* Show All Designs */}
      <div className="space-y-8">
        {designs.map((design) => (
          <div key={design.id} className="space-y-4">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold">{design.name}</h2>
              {selectedDesign === design.id && (
                <Badge className="bg-blue-500 text-white">Selected</Badge>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {design.component}
              {design.component}
              {design.component}
            </div>
            <hr className="my-6" />
          </div>
        ))}
      </div>

      {/* Design Features Comparison */}
      <div className="mt-12">
        <h2 className="text-2xl font-bold mb-4">Design Features Comparison</h2>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="border p-3 text-left">Feature</th>
                <th className="border p-3 text-center">Current</th>
                <th className="border p-3 text-center">Horizontal</th>
                <th className="border p-3 text-center">Minimalist</th>
                <th className="border p-3 text-center">Color Header</th>
                <th className="border p-3 text-center">Ticket</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border p-3">Visual Priority Indicator</td>
                <td className="border p-3 text-center">✅ Left border</td>
                <td className="border p-3 text-center">✅ Icon color</td>
                <td className="border p-3 text-center">✅ Top border</td>
                <td className="border p-3 text-center">✅ Header stripe</td>
                <td className="border p-3 text-center">✅ Gradient icon</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="border p-3">Space Efficiency</td>
                <td className="border p-3 text-center">Medium</td>
                <td className="border p-3 text-center">✅ High</td>
                <td className="border p-3 text-center">✅ High</td>
                <td className="border p-3 text-center">Medium</td>
                <td className="border p-3 text-center">Low</td>
              </tr>
              <tr>
                <td className="border p-3">Information Density</td>
                <td className="border p-3 text-center">✅ Balanced</td>
                <td className="border p-3 text-center">✅ High</td>
                <td className="border p-3 text-center">Medium</td>
                <td className="border p-3 text-center">✅ High</td>
                <td className="border p-3 text-center">Medium</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="border p-3">Visual Impact</td>
                <td className="border p-3 text-center">Medium</td>
                <td className="border p-3 text-center">Low</td>
                <td className="border p-3 text-center">Medium</td>
                <td className="border p-3 text-center">✅ High</td>
                <td className="border p-3 text-center">✅ Very High</td>
              </tr>
              <tr>
                <td className="border p-3">Best For</td>
                <td className="border p-3">General use</td>
                <td className="border p-3">Many insights</td>
                <td className="border p-3">Clean UI</td>
                <td className="border p-3">Category focus</td>
                <td className="border p-3">Premium feel</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Recommendations */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-bold text-lg mb-3 text-blue-900">Recommendations:</h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li><strong>Current Design:</strong> Best for balanced information display</li>
          <li><strong>Horizontal Compact:</strong> Best when you have 50+ insights to display</li>
          <li><strong>Minimalist:</strong> Best for clean, modern dashboard aesthetic</li>
          <li><strong>Color Header:</strong> Best for emphasizing categories with visual coding</li>
          <li><strong>Enhanced Ticket:</strong> Best for premium, professional support ticket feel</li>
        </ul>
      </div>
    </div>
  );
};

export default AIInsightCardDesigns;
